/**
 * Utilities for asset URL construction with cache busting.
 *
 * Handles:
 * - Thumbnail URL generation with width parameter
 * - Cache busting using provided cache key
 * - Null handling for missing assets
 */

import { api } from './client'
import { createNadDebug } from '../utils/debugLog'

const canvasImagePreviewLog = createNadDebug('dw:canvas-image-preview')

/**
 * Get an asset URL with optional cache busting.
 * This is a pure function - no React hooks.
 *
 * @param projectId - The project ID
 * @param assetPath - The asset path (e.g., "assets/characters/liz/portrait.jpg"), or null
 * @param width - Thumbnail width (0 for full size)
 * @param cacheKey - Optional cache key for cache busting (e.g., project.updated_at)
 * @returns The full asset URL with cache busting, or null if assetPath is null/undefined
 */
export function getAssetUrl(
  projectId: string,
  assetPath: string | null | undefined,
  width: number = 0,
  cacheKey?: string
): string | null {
  if (!assetPath) return null
  // Signed Supabase URLs (from published snapshots) are already absolute
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath
  }
  return api.getThumbnailUrl(projectId, assetPath, width, cacheKey)
}

/**
 * Get the first-frame poster URL for a video asset (RFC-0931).
 * This is a pure function - no React hooks.
 *
 * Resolves to the backend `?poster=1` variant of the video URL, which serves
 * the pre-generated `.poster.jpg` sibling or 404s (the caller's `<img>` then
 * falls back to `<video>`). Absolute upload URLs have no poster sibling, so
 * this returns null for them — callers keep today's `<video>` behavior.
 *
 * @param projectId - The project ID
 * @param assetPath - The video asset path, or null
 * @param cacheKey - Optional cache key for cache busting
 * @returns The poster URL, or null if no poster is derivable (absolute/missing)
 */
export function getVideoPosterUrl(
  projectId: string,
  assetPath: string | null | undefined,
  cacheKey?: string
): string | null {
  if (!assetPath) return null
  // Absolute upload URLs have no backend-owned poster sibling.
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return null
  }
  return api.getAssetUrl(projectId, assetPath, { poster: true, bustCache: cacheKey })
}

/**
 * Get a thumbnail URL (convenience wrapper with default width).
 * This is a pure function - no React hooks.
 *
 * @param projectId - The project ID
 * @param assetPath - The asset path, or null
 * @param width - Thumbnail width (default: 300)
 * @param cacheKey - Optional cache key for cache busting
 * @returns The thumbnail URL with cache busting, or null if assetPath is null/undefined
 */
export function getThumbnailUrl(
  projectId: string,
  assetPath: string | null | undefined,
  width: number = 300,
  cacheKey?: string
): string | null {
  return getAssetUrl(projectId, assetPath, width, cacheKey)
}

/**
 * Get the fixed stored rendition used by generated-image canvas cards.
 * Detailed surfaces keep using getAssetUrl() and therefore the original.
 */
export function getCanvasPreviewUrl(
  projectId: string,
  assetPath: string | null | undefined,
  cacheKey?: string,
): string | null {
  if (!assetPath) return null
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath
  }
  const url = api.getAssetUrl(projectId, assetPath, {
    rendition: 'canvas_preview_v1',
    bustCache: cacheKey,
  })
  canvasImagePreviewLog({
    event: 'canvas-image-preview:canvas_url_resolved',
    trace_id: `canvas-image-preview:${projectId}:${assetPath}`,
    project_id: projectId,
    asset_path: assetPath,
    rendition: 'canvas_preview_v1',
  })
  return url
}

// Legacy hook exports for backwards compatibility
// These now just call the pure functions without subscribing to project query
// Components should pass cacheKey from their parent's useProject call

/**
 * @deprecated Use getThumbnailUrl pure function instead and pass cacheKey from parent
 */
export function useThumbnailUrl(
  projectId: string,
  assetPath: string | null | undefined,
  width: number = 300
): string | null {
  // Return URL without cache key - components should migrate to pure function
  return getThumbnailUrl(projectId, assetPath, width)
}

/**
 * @deprecated Use getAssetUrl pure function instead and pass cacheKey from parent
 */
export function useAssetUrl(
  projectId: string,
  assetPath: string | null | undefined,
  options?: { width?: number }
): string | null {
  return getAssetUrl(projectId, assetPath, options?.width ?? 0)
}

/**
 * Get a library asset URL for thumbnails of library-sourced nodes.
 * Library assets are served via /library/assets/{asset_type}/{asset_id}.
 */
export function getLibraryThumbnailUrl(
  assetPath: string | null | undefined,
): string | null {
  if (!assetPath) return null
  if (assetPath.startsWith('http://') || assetPath.startsWith('https://')) {
    return assetPath
  }
  return api.getLibraryAssetUrl(assetPath)
}
